import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Sparkles, Users, Music, Film, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    participantType: 'guest',
    mediaName: '',
    additionalGuests: '0',
    guestNames: ['', '']
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleParticipantTypeChange = (value) => {
    setFormData(prev => ({ ...prev, participantType: value }));
  };

  const handleGuestsChange = (value) => {
    setFormData(prev => ({ ...prev, additionalGuests: value }));
  };

  const handleGuestNameChange = (index, value) => {
    setFormData(prev => {
      const newGuestNames = [...prev.guestNames];
      newGuestNames[index] = value;
      return { ...prev, guestNames: newGuestNames };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    // Validate media name if participant type is media
    if (formData.participantType === 'media' && !formData.mediaName.trim()) {
      toast.error('Veuillez indiquer le nom de votre média.');
      return;
    }

    const numGuests = parseInt(formData.additionalGuests);
    
    // Validate guest names if guests are added
    if (numGuests > 0) {
      for (let i = 0; i < numGuests; i++) {
        if (!formData.guestNames[i] || formData.guestNames[i].trim() === '') {
          toast.error(`Veuillez entrer le nom de l'invité supplémentaire ${i + 1}.`);
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      const guestNamesToSubmit = numGuests > 0 
        ? formData.guestNames.slice(0, numGuests)
        : [];

      const response = await axios.post(`${API}/rsvp`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        business: formData.business,
        participantType: formData.participantType,
        mediaName: formData.participantType === 'media' ? formData.mediaName : '',
        additionalGuests: numGuests,
        guestNames: guestNamesToSubmit
      });

      if (response.data) {
        const isWaitlist = response.data.waitlist;
        
        if (isWaitlist) {
          toast.success(
            'Merci pour votre confirmation ! Vous êtes actuellement sur la liste d\'attente. Nous vous contacterons si une place se libère.',
            {
              duration: 6000,
            }
          );
        } else {
          toast.success(
            'Merci pour votre confirmation ! Nous avons hâte de vous accueillir à cette soirée exclusive. À très bientôt !',
            {
              duration: 5000,
            }
          );
        }
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          business: '',
          participantType: 'guest',
          mediaName: '',
          additionalGuests: '0',
          guestNames: ['', '']
        });
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      const errorMessage = error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section with Event Image */}
      <div className="relative w-full h-[500px] md:h-[700px] overflow-hidden">
        {/* Desktop Image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/e25pelb3_M2MS1_HIGHRES_20.9.png"
          alt="Le Meilleur des Deux Mondes"
          className="hidden md:block w-full h-full object-cover object-top"
        />
        {/* Mobile Image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/9xbc6osh_Instagram_Carre%CC%81.png"
          alt="Le Meilleur des Deux Mondes"
          className="block md:hidden w-full h-full object-cover object-top"
        />
      </div>

      {/* Welcome Section */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-bold mb-8">
            <span className="text-gold">Avant-Première VIP</span> <span className="text-white">Le Meilleur des Deux Mondes</span>
          </h1>
          
          <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
            <p>
              Nous sommes ravis de vous offrir cette <span className="text-gold font-semibold">invitation exclusive</span> à la grande avant-première de la série, en présence de Kassandra Fradette et Luc Poirier. L'événement se déroulera au Musée d'exposition de la collection de voitures exotiques de Luc Poirier.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500/10 to-orange-500/5 border border-orange-500/30 rounded-lg mt-4">
              <span className="text-orange-500 text-2xl">⚠</span>
              <p className="text-orange-500 font-semibold text-base">
                Nombre de places limité — Réservation obligatoire
              </p>
            </div>
          </div>
        </div>

        {/* About the Series Section */}
        <div className="mt-16 pt-12 border-t border-gray-800">
          <h2 className="text-3xl font-bold text-gold mb-6 text-center">
            À propos de la série
          </h2>
          <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
            <p>
              <span className="font-semibold text-white">Le Meilleur des Deux Mondes</span> est une nouvelle série documentaire de 10 épisodes qui repousse les frontières du genre, alliant aventure extrême et luxe raffiné.
            </p>
            <p>
              <span className="text-gold font-semibold">Kassandra Fradette</span> emmène l'entrepreneur <span className="text-gold font-semibold">Luc Poirier</span> à travers le Canada pour vivre des expériences aussi spectaculaires que prestigieuses, dans des décors grandioses où chaque défi met à l'épreuve le corps, l'esprit et la perception même du luxe.
            </p>

            {/* Photo Gallery */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
              <div className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold/40 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/g3ft5wmq_Screenshot%202025-10-26%20at%201.02.32%E2%80%AFAM.png"
                  alt="Kassandra et Luc - Aventure en polo"
                  className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold/40 transition-all">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/skyu2ybu_Screenshot%202025-10-26%20at%2012.57.57%E2%80%AFAM.png"
                  alt="Expérience culinaire et luxe"
                  className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>

            {/* Diffusion Info with Logos */}
            <div className="flex flex-col items-center mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-300 text-lg mb-4">Diffusion 6 janvier 2026</p>
              <div className="flex items-center gap-6 flex-wrap justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/t97kbcy7_RDS_logo.svg.png"
                  alt="RDS"
                  className="h-6 md:h-8 w-auto opacity-80 hover:opacity-100 transition-opacity"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/zlsrdcov_Crave_2018_logo.svg%20%282%29.png"
                  alt="CRAVE"
                  className="h-6 md:h-8 w-auto opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Event Details Section */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* Left Column - Event Info */}
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <Calendar className="w-6 h-6 text-gold mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gold mb-1">Date</h3>
                <p className="text-gray-300">Lundi 1er décembre</p>
                <p className="text-gray-300">18 h 30</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-gold mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gold mb-1">Lieu</h3>
                <p className="text-gray-300">Musée d'exposition de la collection</p>
                <p className="text-gray-300">de voitures exotiques de Luc Poirier</p>
                <p className="text-gray-300 mt-2 italic">L'adresse exacte vous sera communiquée</p>
                <p className="text-gray-300 italic">par courriel après votre confirmation</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Sparkles className="w-6 h-6 text-gold mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gold mb-1">Code vestimentaire</h3>
                <p className="text-gray-300">Chic tapis rouge et audacieux</p>
                <p className="text-sm text-gray-400 mt-1">(Éviter talons aiguille)</p>
              </div>
            </div>
          </div>

          {/* Right Column - Program */}
          <div className="bg-gray-900/50 p-6 rounded-lg border border-gold/20">
            <h3 className="text-xl font-semibold text-gold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Déroulement de la soirée
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Users className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">Accueil & cocktail dînatoire avec bouchées raffinées</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">Photobooth & expérience immersive au cœur du musée</span>
              </li>
              <li className="flex items-start gap-3">
                <Mic className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">Présentation du projet avec Kassandra, Luc et l'équipe</span>
              </li>
              <li className="flex items-start gap-3">
                <Film className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">Projection des deux premiers épisodes</span>
              </li>
              <li className="flex items-start gap-3">
                <Music className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">DJ-violoniste & réseautage</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span className="text-gray-300">Encan silencieux</span>
              </li>
            </ul>
          </div>
        </div>

        {/* RSVP Form Section */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-gold/30 rounded-lg p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gold mb-2 text-center">Confirmez votre présence</h2>
          <p className="text-gray-400 text-center mb-8">Réservez votre place pour cette soirée exclusive</p>
          
          <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
            <div>
              <Label htmlFor="name" className="text-gray-200">Nom complet *</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Votre nom"
                className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-200">Courriel *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="votre.email@exemple.com"
                className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-gray-200">Téléphone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 514 555 0123"
                className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold"
                required
              />
            </div>

            <div>
              <Label htmlFor="business" className="text-gray-200">Entreprise (optionnel)</Label>
              <Input
                id="business"
                name="business"
                type="text"
                value={formData.business}
                onChange={handleInputChange}
                placeholder="Nom de votre entreprise"
                className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold"
              />
            </div>

            <div>
              <Label htmlFor="participantType" className="text-gray-200">Type de participant *</Label>
              <Select value={formData.participantType} onValueChange={handleParticipantTypeChange}>
                <SelectTrigger className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-950 border-gray-700">
                  <SelectItem value="guest" className="text-white hover:bg-gray-800">Invité</SelectItem>
                  <SelectItem value="media" className="text-white hover:bg-gray-800">Média</SelectItem>
                  <SelectItem value="volunteer" className="text-white hover:bg-gray-800">Bénévole</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Media Name Field - Only shown when Media is selected */}
            {formData.participantType === 'media' && (
              <div className="p-4 bg-gray-950/50 rounded-lg border border-gold/20">
                <Label htmlFor="mediaName" className="text-gray-200">Nom du média *</Label>
                <Input
                  id="mediaName"
                  name="mediaName"
                  type="text"
                  value={formData.mediaName}
                  onChange={handleInputChange}
                  placeholder="Ex: Le Journal de Montréal, TVA Nouvelles, etc."
                  className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold"
                  required
                />
              </div>
            )}

            <div>
              <Label htmlFor="additionalGuests" className="text-gray-200">Nombre d'invités supplémentaires</Label>
              <Select value={formData.additionalGuests} onValueChange={handleGuestsChange}>
                <SelectTrigger className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-950 border-gray-700">
                  <SelectItem value="0" className="text-white hover:bg-gray-800">Moi seulement</SelectItem>
                  <SelectItem value="1" className="text-white hover:bg-gray-800">1 invité supplémentaire</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-2">Maximum 1 invité supplémentaire</p>
            </div>

            {/* Guest Names Fields */}
            {parseInt(formData.additionalGuests) > 0 && (
              <div className="space-y-4 p-4 bg-gray-950/50 rounded-lg border border-gold/20">
                <h3 className="text-lg font-semibold text-gold">Noms des invités supplémentaires</h3>
                {Array.from({ length: parseInt(formData.additionalGuests) }).map((_, index) => (
                  <div key={index}>
                    <Label htmlFor={`guest-${index}`} className="text-gray-200">
                      Invité supplémentaire {index + 1} *
                    </Label>
                    <Input
                      id={`guest-${index}`}
                      type="text"
                      value={formData.guestNames[index]}
                      onChange={(e) => handleGuestNameChange(index, e.target.value)}
                      placeholder={`Nom complet de l'invité supplémentaire ${index + 1}`}
                      className="mt-2 bg-gray-950 border-gray-700 text-white focus:border-gold"
                      required
                    />
                  </div>
                ))}
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gold hover:bg-gold/90 text-black font-semibold py-6 text-lg transition-colors duration-300"
            >
              {isSubmitting ? 'Envoi en cours...' : 'Confirmer ma présence'}
            </Button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500">
          <p>© 2024 Le Meilleur des Deux Mondes. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;